import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { postsAPI, clientsAPI } from '../../services/api';
import { Card, Button } from '../ui';
import Loading from '../common/Loading';
import { handleApiError, showToast } from '../../utils/helpers';
import { FileText, Plus, ChevronRight, PenLine, Building2, User } from 'lucide-react';

const ArticlesListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isRTL } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [postsRes, clientsRes] = await Promise.all([
        postsAPI.getAll({ content_type: 'blog' }),
        clientsAPI.getAll()
      ]);
      setArticles(postsRes.data.posts || []);
      setClients(clientsRes.data.clients || []);
    } catch (err) {
      console.error('Failed to fetch articles:', err);
      showToast.error(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = articles.filter(a => {
    if (filterClient && a.client_id !== filterClient) return false;
    if (filterStatus && a.status !== filterStatus) return false;
    return true;
  });

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      needs_changes: 'bg-rose-50 text-rose-700 border-rose-200',
      scheduled: 'bg-sky-50 text-sky-700 border-sky-200',
      published: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    const labels = {
      pending: isRTL ? 'قيد المراجعة' : 'Pending',
      approved: isRTL ? 'معتمد' : 'Approved',
      needs_changes: isRTL ? 'يحتاج تعديل' : 'Needs Changes',
      scheduled: isRTL ? 'مجدول' : 'Scheduled',
      published: isRTL ? 'منشور' : 'Published'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colors[status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) return <Loading fullScreen />;

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-50 via-white to-sky-50/50 border border-slate-200/80 p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-sky-500 to-emerald-500 text-white shadow-lg shadow-sky-500/25">
              <PenLine className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                {isRTL ? 'قسم التحرير' : 'Editor'}
              </h1>
              <p className="text-slate-600 mt-0.5 text-sm">
                {isRTL ? 'إدارة المقالات والمحتوى النصي' : 'Manage articles and written content'}
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate('/articles/new')}
            className="flex items-center gap-2 shadow-md hover:shadow-lg transition-shadow bg-gradient-to-r from-sky-600 to-emerald-600 hover:from-sky-700 hover:to-emerald-700 border-0"
          >
            <Plus className="w-5 h-5" />
            {isRTL ? 'مقالة جديدة' : 'New Article'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 rounded-xl border-slate-200/80 shadow-sm">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="min-w-[180px]">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              {isRTL ? 'العميل' : 'Client'}
            </label>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="input text-sm rounded-lg border-slate-200"
            >
              <option value="">{isRTL ? 'الكل' : 'All'}</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>
          </div>
          <div className="min-w-[140px]">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              {isRTL ? 'الحالة' : 'Status'}
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input text-sm rounded-lg border-slate-200"
            >
              <option value="">{isRTL ? 'الكل' : 'All'}</option>
              <option value="pending">{isRTL ? 'قيد المراجعة' : 'Pending'}</option>
              <option value="approved">{isRTL ? 'معتمد' : 'Approved'}</option>
              <option value="needs_changes">{isRTL ? 'يحتاج تعديل' : 'Needs Changes'}</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Articles list */}
      <Card className="overflow-hidden rounded-xl border-slate-200/80 shadow-sm">
        {filteredArticles.length === 0 ? (
          <div className="p-16 text-center">
            <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center">
              <FileText className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-700 mb-2">
              {isRTL ? 'لا توجد مقالات بعد' : 'No articles yet'}
            </h3>
            <p className="text-slate-500 mb-6 max-w-sm mx-auto">
              {isRTL ? 'ابدأ بإنشاء أول مقالة لك واستخدم محرر النصوص الغني لتنسيق المحتوى' : 'Start by creating your first article and use the rich text editor to format your content'}
            </p>
            <Button
              variant="primary"
              onClick={() => navigate('/articles/new')}
              className="bg-gradient-to-r from-sky-600 to-emerald-600 hover:from-sky-700 hover:to-emerald-700 border-0"
            >
              <Plus className="w-4 h-4 inline-block mr-2" />
              {isRTL ? 'إنشاء مقالة' : 'Create Article'}
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/articles/${article.id}/edit`}
                className={`flex items-center justify-between p-5 hover:bg-slate-50/80 transition-all duration-200 group ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap mb-1.5">
                    <h3 className="font-semibold text-slate-900 truncate group-hover:text-sky-700 transition-colors">
                      {article.title}
                    </h3>
                    {getStatusBadge(article.status)}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5" />
                      {article.clients?.company_name || '-'}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5" />
                      {article.users_posts_assigned_writer_idTousers?.name || article.users_posts_created_byTousers?.name || '-'}
                    </span>
                  </div>
                  {article.rich_description && (
                    <p className="text-sm text-slate-600 mt-2 line-clamp-2">
                      {article.rich_description.replace(/<[^>]*>/g, ' ').slice(0, 160)}
                      {article.rich_description.length > 160 ? '...' : ''}
                    </p>
                  )}
                </div>
                <ChevronRight className={`w-5 h-5 text-slate-300 group-hover:text-sky-500 flex-shrink-0 transition-colors ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ArticlesListPage;
